import { get, isUndefined } from 'lodash';
import {
    all,
    call,
    fork,
    put,
    takeLatest,
} from 'redux-saga/effects';
import { _queries as queries } from '../../graphql/queries.graphql';
import {
    API_NAME,
} from '../../config/config';
import { DynamicObject } from '../../utils/commonInterfaces';
import client, { post, delApi } from 'src/api';
import {
    getWebhooksConfigurationRequestAction,
    getWebhooksConfigurationSuccessAction,
    getWebhooksConfigurationFailedAction,
    saveWebhooksConfigurationRequestAction,
    saveWebhooksConfigurationResponseAction,
    generateHMACSignatureRequestAction,
    generateHMACSignatureResponseAction,
    rotateHMACSignatureRequestAction,
    rotateHMACSignatureResponseAction,
    removeHMACSignatureRequestAction,
    removeHMACSignatureResponseAction,
    generateAESKeyRequestAction,
    generateAESKeyResponseAction,
    rotateAESKeyRequestAction,
    rotateAESKeyResponseAction,
    removeAESKeyRequestAction,
    removeAESKeyResponseAction,
} from './webhookConfigurationSlice';
import { 
    CompanyWebhooksConfiguration 
} from './types';

/**
 * Function responsible for getting webhook configuration for a company from API.
 */
function* handleGetWebhooksConfigurationRequest() {
    try {

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_COMPANY_WEBHOOKS
            }
        );

        const webhookData: CompanyWebhooksConfiguration = get(res.data, 'GetCompanyWebhooks', null);

        yield put(getWebhooksConfigurationSuccessAction(webhookData));
    } catch (err) {
        let errorMessage = 'Failed to load webhook configuration';
        
        if (err instanceof Error) {
            errorMessage = err.message;
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.', err);
        }
        
        yield put(getWebhooksConfigurationFailedAction(errorMessage));
    }
}

/**
 * Function responsible for saving webhook configuration to API.
 */
function* handleSaveWebhooksConfigurationRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    
    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/company/webhooks/save',
            options: {
                body: payload,
            }
        });
        
        yield put(saveWebhooksConfigurationResponseAction());
        
        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: ['Webhook configuration saved successfully']
            };
            callback(response);
        }
    } catch (err) {
        yield put(saveWebhooksConfigurationResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for generating HMAC signature for a company.
 */
function* handleGenerateHMACSignatureRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/company/hmac',
            options: {}
        });
        
        // Parse the response body (ReadableStream -> JSON)
        const bodyText: string = yield call([res.body, res.body.text]);
        const parsedBody = bodyText ? JSON.parse(bodyText) : null;
        
        yield put(generateHMACSignatureResponseAction());
        
        if (callback) {
            const returnData = {
                IsSuccess: true,
                ...parsedBody,
            };
            callback(returnData);
        }
    } catch (err) {
        yield put(generateHMACSignatureResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { ErrorMessages: [err.message] };
            } else {
                returnData = { ErrorMessages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for rotating HMAC signature for a company.
 */
function* handleRotateHMACSignatureRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/company/hmac/rotate',
            options: {}
        });
        
        // Parse the response body (ReadableStream -> JSON)
        const bodyText: string = yield call([res.body, res.body.text]);
        const parsedBody = bodyText ? JSON.parse(bodyText) : null;
        
        yield put(rotateHMACSignatureResponseAction());
        
        if (callback) {
            const returnData = {
                IsSuccess: true,
                ...parsedBody,
            };
            callback(returnData);
        }
    } catch (err) {
        yield put(rotateHMACSignatureResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { ErrorMessages: [err.message] };
            } else {
                returnData = { ErrorMessages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for removing HMAC signature for a company.
 */
function* handleRemoveHMACSignatureRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        yield call(delApi, 
            '/company/hmac',
            {}
        );
        
        yield put(removeHMACSignatureResponseAction());
        
        // Refresh webhook configuration to get updated IsHMACEnabled status
        // yield put(getWebhooksConfigurationSuccessAction(yield call(
        //     [API, 'graphql'],
        //     graphqlOperation(queries.GET_COMPANY_WEBHOOKS)
        // ).then((res: DynamicObject) => get(res.data, 'GetCompanyWebhooks', null))));
        
        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: ['HMAC validation has been disabled for all webhooks.']
            };
            callback(response);
        }
    } catch (err) {
        yield put(removeHMACSignatureResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for generating AES key for a company.
 */
function* handleGenerateAESKeyRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/company/aes',
            options: {}
        });
        
        // Parse the response body (ReadableStream -> JSON)
        const bodyText: string = yield call([res.body, res.body.text]);
        const parsedBody = bodyText ? JSON.parse(bodyText) : null;
        
        yield put(generateAESKeyResponseAction());
        
        if (callback) {
            const returnData = {
                IsSuccess: true,
                ...parsedBody,
            };
            callback(returnData);
        }
    } catch (err) {
        yield put(generateAESKeyResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { ErrorMessages: [err.message] };
            } else {
                returnData = { ErrorMessages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for rotating AES key for a company.
 */
function* handleRotateAESKeyRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/company/aes/rotate',
            options: {}
        });
        
        // Parse the response body (ReadableStream -> JSON)
        const bodyText: string = yield call([res.body, res.body.text]);
        const parsedBody = bodyText ? JSON.parse(bodyText) : null;
        
        yield put(rotateAESKeyResponseAction());
        
        if (callback) {
            const returnData = {
                IsSuccess: true,
                ...parsedBody,
            };
            callback(returnData);
        }
    } catch (err) {
        yield put(rotateAESKeyResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { ErrorMessages: [err.message] };
            } else {
                returnData = { ErrorMessages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for removing AES key for a company.
 */
function* handleRemoveAESKeyRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    
    try {
        yield call(delApi, 
            '/company/aes',
            {}
        );
        
        yield put(removeAESKeyResponseAction());
        
        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: ['AES key removed successfully']
            };
            callback(response);
        }
    } catch (err) {
        yield put(removeAESKeyResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && !isUndefined(get((err as any), 'response.data'))) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

// Watcher functions
function* watchGetWebhooksConfigurationRequest() {
    yield takeLatest(
        getWebhooksConfigurationRequestAction.type,
        handleGetWebhooksConfigurationRequest
    );
}

function* watchSaveWebhooksConfigurationRequest() {
    yield takeLatest(
        saveWebhooksConfigurationRequestAction.type,
        handleSaveWebhooksConfigurationRequest
    );
}

function* watchGenerateHMACSignatureRequest() {
    yield takeLatest(
        generateHMACSignatureRequestAction.type,
        handleGenerateHMACSignatureRequest
    );
}

function* watchRotateHMACSignatureRequest() {
    yield takeLatest(
        rotateHMACSignatureRequestAction.type,
        handleRotateHMACSignatureRequest
    );
}

function* watchRemoveHMACSignatureRequest() {
    yield takeLatest(
        removeHMACSignatureRequestAction.type,
        handleRemoveHMACSignatureRequest
    );
}

function* watchGenerateAESKeyRequest() {
    yield takeLatest(
        generateAESKeyRequestAction.type,
        handleGenerateAESKeyRequest
    );
}

function* watchRotateAESKeyRequest() {
    yield takeLatest(
        rotateAESKeyRequestAction.type,
        handleRotateAESKeyRequest
    );
}

function* watchRemoveAESKeyRequest() {
    yield takeLatest(
        removeAESKeyRequestAction.type,
        handleRemoveAESKeyRequest
    );
}

// Main saga
function* webhookConfigurationSaga() {
    yield all([
        fork(watchGetWebhooksConfigurationRequest),
        fork(watchSaveWebhooksConfigurationRequest),
        fork(watchGenerateHMACSignatureRequest),
        fork(watchRotateHMACSignatureRequest),
        fork(watchRemoveHMACSignatureRequest),
        fork(watchGenerateAESKeyRequest),
        fork(watchRotateAESKeyRequest),
        fork(watchRemoveAESKeyRequest),
    ]);
}

export default webhookConfigurationSaga;
